import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/Header';
import { SEO } from '@/components/SEO';
import { ShareListingButton } from '@/components/ShareListingButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar as CalendarCmp } from '@/components/ui/calendar';
import PhotoLightbox from '@/components/PhotoLightbox';
import { useAuth } from '@/contexts/AuthContext';
import { MapPin, Info, Calendar, ArrowLeft, Clock, ChevronDown } from 'lucide-react';
import { fr, enUS } from 'date-fns/locale';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { pickTranslation } from '@/i18n/pickTranslation';
import { translateServerError } from '@/lib/serverErrors';
import { numberLocale } from '@/lib/format';
import { depositAmount, listingDepositPct } from '@/lib/transportDeposit';
import { BookingRequestSentDialog } from '@/components/BookingRequestSentDialog';
import { RidaloCheckbox } from '@/components/ui/ridalo-checkbox';
import { MAX_LUGGAGE, MAX_ROOM, PICKUP_AFTER_LANDING_MINUTES, DIRECTION_ZONE_TO_ZONE, cleanMapsUrl, clockLabel, departureChoices, destinationChoices, findRoute, listingRoutes, minNoticeHours, minNoticeViolation, normalizeFlight, pickupAfterLanding, routeDirection, validatePickup, vehicleCapacity, zoneLabel } from '@/lib/transferRoute';
import { KRABI_TIERS } from '@/lib/krabiTransfers';
import { PickupInfoNotice } from '@/components/PickupInfoNotice';
import { ServiceReviewsSection } from '@/components/ServiceReviewsSection';
import { MobileBookingBar } from '@/components/MobileBookingBar';
import { ageBandLabels } from '@/lib/excursionAges';
import { listingOffer, offerJsonLd } from '@/lib/listingOffer';
import { businessNameOf } from '@/lib/providerName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Transport is 24/7 — full-day slots in 30-minute steps.
const TRANSPORT_HOUR_SLOTS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  return out;
})();

// Calendar day → "YYYY-MM-DD" (local date parts, no UTC shift).
const localYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Today's date in Phuket, whatever the traveller's device time zone.
const bangkokYmd = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

// Whole days a provider blocked on a listing: legacy blocked_dates, plus the
// days entirely covered by a blocked_periods entry ("YYYY-MM-DDTHH:MM", Phuket
// time). A partly blocked day stays selectable; the server checks the slot.
const blockedWholeDays = (service) => {
  const days = new Set((service?.blocked_dates || []).filter(Boolean));
  const parse = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(v || ''));
    return m ? { day: Date.UTC(+m[1], +m[2] - 1, +m[3]), minutes: +m[4] * 60 + +m[5] } : null;
  };
  const DAY = 86400000;
  for (const p of service?.blocked_periods || []) {
    const s = parse(p?.start); const e = parse(p?.end);
    if (!s || !e) continue;
    const first = s.minutes === 0 ? s.day : s.day + DAY;
    const last = e.minutes >= 23 * 60 + 59 ? e.day : e.day - DAY;   // an end at 00:00 frees that day
    for (let d = first; d <= last && d - first <= 400 * DAY; d += DAY) days.add(new Date(d).toISOString().slice(0, 10));
  }
  return days;
};

export const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? enUS : fr;
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('10:00');
  const [passengers, setPassengers] = useState(2);
  const [bookingDetails, setBookingDetails] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  // « Demande envoyée ! » once the server has created the request.
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fromZoneId, setFromZoneId] = useState('');
  const [toZoneId, setToZoneId] = useState('');
  const [zones, setZones] = useState([]);
  // Private Driver V1 — duration selection. Pre-filled from ?duration=X URL param.
  const [selectedDuration, setSelectedDuration] = useState(8);
  // Excursion V1 — passenger breakdown, pickup zone and departure time.
  const [adults, setAdults] = useState(1);   // excursions are priced per person: one adult by default
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [pickupZone, setPickupZone] = useState('');
  // Excursion hotel pickup: hotel required, room number optional.
  const [hotelName, setHotelName] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [excursionCalendarOpen, setExcursionCalendarOpen] = useState(false);
  // Excursion date picker: a click (or tap) outside it closes it; the date
  // already chosen is kept. Escape is handled by the booking window below.
  const excursionCalendarRef = useRef(null);
  useEffect(() => {
    if (!excursionCalendarOpen) return undefined;
    const onPointerDown = (e) => {
      if (excursionCalendarRef.current && !excursionCalendarRef.current.contains(e.target)) setExcursionCalendarOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [excursionCalendarOpen]);
  const [lightboxIndex, setLightboxIndex] = useState(null);   // excursion photo gallery (null = closed)
  // Guest-booking flow — see Scooter reference (ScooterDetail.js). We open an
  // auth prompt on submit when the visitor is not logged in and persist the
  // full form draft in localStorage so it resurfaces after login/register.
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  useEffect(() => {
    // Guard: si l'URL contient une catégorie
    if (serviceId === 'scooter') {
      // ✱ Unification : la catégorie scooter a sa propre marketplace V1
      navigate('/scooters', { replace: true });
      return;
    }
    const CATEGORY_SLUGS = ['transfer', 'excursion'];
    if (CATEGORY_SLUGS.includes(serviceId)) {
      navigate(`/services?category=${serviceId}`, { replace: true });
      return;
    }
    // Pre-fill from ?date=&time=&pax=&from_zone_id=&to_zone_id=&duration= (Transport marketplace deep-link)
    const params = new URLSearchParams(window.location.search);
    if (params.get('date')) setBookingDate(params.get('date'));
    if (params.get('time')) setBookingTime(params.get('time'));
    if (params.get('pax')) setPassengers(parseInt(params.get('pax')) || 2);
    if (params.get('from_zone_id')) setFromZoneId(params.get('from_zone_id'));
    if (params.get('to_zone_id')) setToZoneId(params.get('to_zone_id'));
    if (params.get('duration')) setSelectedDuration(parseInt(params.get('duration')) || 8);
    if (params.get('adults')) setAdults(parseInt(params.get('adults')) || 1);
    if (params.get('children')) setChildren(parseInt(params.get('children')) || 0);
    if (params.get('infants')) setInfants(parseInt(params.get('infants')) || 0);
    // Prefetch zones so we can render zone names on the airport route summary.
    axios.get(`${API}/zones`).then((r) => setZones(r.data || [])).catch(() => {});
    fetchService();
  }, [serviceId]);

  const fetchService = async () => {
    setLoading(true);
    try {
      // Credentials let a provider preview its own unpublished listing.
      const { data } = await axios.get(`${API}/services/${serviceId}`, { withCredentials: true });
      // ✱ Unification : les scooters passent exclusivement par la marketplace V1 /scooters
      if (data?.category === 'scooter') {
        navigate(`/scooters/${serviceId}`, { replace: true });
        return;
      }
      setService(data);
    } catch (error) {
      console.error('Error fetching service:', error);
      toast.error(t('serviceDetail.notFound'));
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  // Helper: Normalise les URLs images pour JSON-LD (remplace Railway/Preview/Assets par ridalo.co)
  const normalizeImageUrlForJsonLd = (url) => {
    if (!url) return url;
    // Ne transformer que les URLs internes Ridalo/Emergent, garder les URLs externes (Pexels, etc.)
    if (url.includes('railway.app') || url.includes('emergentagent.com')) {
      return url
        .replace(/https?:\/\/ridalo-production\.up\.railway\.app/g, 'https://ridalo.co')
        .replace(/https?:\/\/[^/]*\.emergentagent\.com/g, 'https://ridalo.co');
    }
    return url; // Garder les URLs externes telles quelles (Pexels, etc.)
  };

  // JSON-LD Service/TouristTrip + BreadcrumbList
  React.useEffect(() => {
    if (!service || !serviceId) return;

    const schemaType = service.category === 'excursion' ? 'TouristTrip' : 'Service';
    
    const serviceLD = {
      "@context": "https://schema.org",
      "@type": schemaType,
      "name": service.title,
      "description": service.description,
      "image": service.images ? service.images.map(normalizeImageUrlForJsonLd) : [],
      ...(businessNameOf(service) ? { "provider": {
        "@type": "Organization",
        "name": businessNameOf(service)
      } } : {}),
      // The prices the page shows (per zone / route / duration), not the stored price alone.
      "offers": offerJsonLd(listingOffer(service), `https://ridalo.co/services/${serviceId}`)
    };
    if (!serviceLD.offers) delete serviceLD.offers;

    // Ajouter destination pour excursions
    if (service.category === 'excursion' && service.details?.excursion?.destination) {
      serviceLD.touristType = "Tourist";
      serviceLD.itinerary = {
        "@type": "ItemList",
        "itemListElement": [{ "@type": "ListItem", "position": 1, "item": { "@type": "Place", "name": service.details.excursion.destination } }]
      };
    }

    // BreadcrumbList
    const categoryName = service.category === 'excursion' ? t('seo.breadcrumb.excursions') :
                         service.category === 'transfer' ? t('seo.breadcrumb.transport') :
                         t('seo.breadcrumb.services');
    const categoryPath = service.category === 'excursion' ? '/services?category=excursion' : 
                         service.category === 'transfer' ? '/transport' : 
                         '/services';

    const breadcrumbLD = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": t('seo.breadcrumb.home'),
          "item": "https://ridalo.co/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": categoryName,
          "item": `https://ridalo.co${categoryPath}`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": service.title,
          "item": `https://ridalo.co/services/${serviceId}`
        }
      ]
    };

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.text = JSON.stringify(serviceLD);
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.text = JSON.stringify(breadcrumbLD);
    document.head.appendChild(s2);

    return () => {
      if (s1.parentNode) s1.remove();
      if (s2.parentNode) s2.remove();
    };
  }, [service?.service_id, serviceId, t]);


  // Compute matched price for airport transfers based on ?from_zone_id/?to_zone_id.
  const PHUKET_AIRPORT_ZONE_ID = 'zone_phuket_airport';
  const airportTransfers = (((service?.details || {}).transport) || {}).airport_transfers || [];
  const isAirportRoute = !!(fromZoneId && toZoneId && (fromZoneId === PHUKET_AIRPORT_ZONE_ID || toZoneId === PHUKET_AIRPORT_ZONE_ID));
  const destinationZoneId = isAirportRoute
    ? (fromZoneId === PHUKET_AIRPORT_ZONE_ID ? toZoneId : fromZoneId)
    : null;
  const direction = isAirportRoute
    ? (fromZoneId === PHUKET_AIRPORT_ZONE_ID ? 'from_airport' : 'to_airport')
    : null;
  const matchedTransfer = destinationZoneId
    ? airportTransfers.find((t) => t.zone_id === destinationZoneId)
    : null;
  // Every route the listing offers (airport zones, Phuket ↔ Krabi) with its price.
  const transferRoutes = React.useMemo(() => listingRoutes(service), [service]);
  const urlRoute = findRoute(transferRoutes, fromZoneId, toZoneId);
  const matchedPrice = urlRoute ? urlRoute.price : (matchedTransfer
    ? (direction === 'from_airport'
        ? (matchedTransfer.price_from_airport ?? matchedTransfer.price)
        : (matchedTransfer.price_to_airport ?? matchedTransfer.price))
    : null);
  const zoneById = React.useMemo(() => Object.fromEntries(zones.map((z) => [z.zone_id, z])), [zones]);
  const isTransfer = service?.category === 'transfer';
  const isPrivateDriver = service?.category === 'private_driver';
  const isExcursion = service?.category === 'excursion';
  // Age bands of THIS excursion (its free age, or the historical 1-2 / 3-11 labels).
  const ageLabels = ageBandLabels(t, service?.details?.excursion?.free_under_age);
  // Excursion V1 — extract config and compute total price server-consistent.
  const excursionCfg = isExcursion ? (((service?.details || {}).excursion) || {}) : {};
  const excursionPricing = excursionCfg.pricing || {};
  const excursionPickupAvailable = !!excursionCfg.pickup_available;
  const excursionPickupZones = Array.isArray(excursionCfg.pickup_zones) ? excursionCfg.pickup_zones : [];
  // Hotel pickup offered: same rule as the server (hotel name required).
  const excursionHotelPickup = excursionPickupAvailable && excursionPickupZones.length > 0;
  const excursionDepartureTimes = Array.isArray(excursionCfg.departure_times)
    ? excursionCfg.departure_times.filter(Boolean)
    : [];
  const excursionMaxParticipants = excursionCfg.max_participants || null;
  // Today in Phuket (Asia/Bangkok) as a local midnight, and the whole days the
  // provider blocked — same sources as the server check.
  const excursionToday = React.useMemo(() => new Date(`${bangkokYmd()}T00:00:00`), []);
  const excursionBlockedDays = React.useMemo(
    () => (isExcursion ? blockedWholeDays(service) : new Set()),
    [isExcursion, service],
  );
  const excursionHasOpenDay = React.useMemo(() => {
    const d = new Date(excursionToday);
    for (let i = 0; i < 366; i += 1, d.setDate(d.getDate() + 1)) {
      if (!excursionBlockedDays.has(localYmd(d))) return true;
    }
    return false;
  }, [excursionToday, excursionBlockedDays]);
  const excursionZonePrice = excursionPricing.price_by_zone && pickupZone
    ? (excursionPricing.zone_prices || []).find((zp) => zp.zone === pickupZone)
    : null;
  const excursionAdultPrice = isExcursion
    ? (excursionPricing.price_by_zone
        ? (excursionZonePrice?.adult_price ?? null)
        : (excursionPricing.adult_price ?? null))
    : null;
  const excursionChildPrice = isExcursion
    ? (excursionPricing.price_by_zone
        ? (excursionZonePrice?.child_price ?? excursionZonePrice?.adult_price ?? null)
        : (excursionPricing.child_price ?? excursionPricing.adult_price ?? null))
    : null;
  // A child price the provider removed is absent (null): children then pay the
  // adult price, as computed by the server — shown as such, never as a
  // separate child price. 0 stays a real (free) child price.
  const excursionChildPriceSet = isExcursion && (excursionPricing.price_by_zone
    ? excursionZonePrice?.child_price != null
    : excursionPricing.child_price != null);
  const excursionTotal = isExcursion && excursionAdultPrice != null
    ? Number(adults) * Number(excursionAdultPrice)
      + Number(children) * Number(excursionChildPrice ?? excursionAdultPrice)
    : null;
  const airportMinPrice = airportTransfers.length > 0
    ? Math.min(...airportTransfers.map((t) => Number(t.price)).filter((p) => Number.isFinite(p) && p > 0))
    : null;
  // "Starting from" mode when no specific route has been picked yet.
  const isStartingFrom = isTransfer && matchedPrice == null && airportMinPrice != null;

  // Private Driver V1 — multi-duration pricing. Extract enabled durations and
  // compute the price for the currently selected duration (booking flow).
  const privateDriverDurations = isPrivateDriver
    ? (((service?.details || {}).transport || {}).durations || [])
    : [];
  const availableDurations = privateDriverDurations.filter((d) => d.duration_hours && d.price);
  const selectedDurationObj = availableDurations.find((d) => d.duration_hours === selectedDuration);
  const privateDriverPrice = selectedDurationObj?.price ?? null;
  // The pre-selected duration must be one the driver offers.
  useEffect(() => {
    if (availableDurations.length && !selectedDurationObj) setSelectedDuration(availableDurations[0].duration_hours);
  }, [availableDurations, selectedDurationObj]);
  // Private Driver minimum price for "À partir de" display (hero/sidebar).
  const privateDriverMinPrice = availableDurations.length > 0
    ? Math.min(...availableDurations.map((d) => Number(d.price)).filter((p) => Number.isFinite(p) && p > 0))
    : null;
  const isPrivateDriverStartingFrom = isPrivateDriver && matchedPrice == null && privateDriverMinPrice != null;

  const displayPrice = matchedPrice != null
    ? matchedPrice
    : (isPrivateDriverStartingFrom
        ? privateDriverMinPrice
        : (isStartingFrom ? airportMinPrice : service?.price));
  const displayRoute = (isAirportRoute || urlRoute) ? {
    from: zoneById[fromZoneId]?.name || (fromZoneId === PHUKET_AIRPORT_ZONE_ID ? 'Phuket Airport' : fromZoneId),
    to: zoneById[toZoneId]?.name || (toZoneId === PHUKET_AIRPORT_ZONE_ID ? 'Phuket Airport' : toZoneId),
  } : null;
  // Deposit config surfaced to the booking dialog (Sans acompte / 25 % / 50 %).
  const paymentConfig = (service?.details || {}).payment_config || {};
  // Excursions: the payment the server asks once the provider accepts —
  // 25 % or 50 % when the provider chose a deposit, else the full price (same
  // rule as the booking, server.py « Excursions V1 »). Nothing is paid when
  // the request is sent.
  const excursionPaymentPct = [25, 50].includes(Number(paymentConfig.deposit_percentage))
    ? Number(paymentConfig.deposit_percentage) : 100;
  // Transfers and private drivers: the deposit the server will ask once the
  // provider accepts (computed by the server, shown before the request is sent).
  const transportDepositPct = (isTransfer || isPrivateDriver) ? listingDepositPct(service) : 0;
  // In-dialog zone picker state — used only when the user opens the booking
  // dialog without a pre-selected airport route.
  // Route chosen in the booking form: departure then destination (only the
  // routes the listing offers; airport zones and Phuket ↔ Krabi).
  const [dialogFrom, setDialogFrom] = useState('');
  const [dialogTo, setDialogTo] = useState('');
  useEffect(() => {
    // Sync in-dialog picker to URL-driven selection when available so the
    // "Réserver maintenant" flow always carries a zone.
    if (urlRoute) {
      setDialogFrom(urlRoute.from_zone_id);
      setDialogTo(urlRoute.to_zone_id);
    }
  }, [urlRoute]);
  // The booking form is the source of the traveller's final choice: the search
  // (URL) only pre-fills it. Same price rule as the server (direction-specific
  // price first, else the zone price); the server recomputes it anyway.
  const hasAirportZones = isTransfer && transferRoutes.length > 0;
  // Capacity declared for THIS vehicle (transport listings): passengers from 1
  // to its maximum, luggage up to its maximum. The server checks them again.
  const isTransportListing = isTransfer || isPrivateDriver;
  const capacity = isTransportListing ? vehicleCapacity(service) : { passengersMax: null, luggageMax: null };
  const paxError = !isTransportListing ? null
    : (!Number.isInteger(passengers) || passengers < 1) ? 'paxMin'
      : (capacity.passengersMax && passengers > capacity.passengersMax) ? 'paxOverCapacity' : null;
  const effectiveTransfer = hasAirportZones ? findRoute(transferRoutes, dialogFrom, dialogTo) : null;
  const effectiveFromZoneId = isTransfer
    ? (hasAirportZones ? (effectiveTransfer ? dialogFrom : null) : (isAirportRoute ? fromZoneId : null))
    : null;
  const effectiveToZoneId = isTransfer
    ? (hasAirportZones ? (effectiveTransfer ? dialogTo : null) : (isAirportRoute ? toZoneId : null))
    : null;
  const effectivePrice = effectiveTransfer ? effectiveTransfer.price : null;
  // Pickup information asked: from the chosen route (airport → hotel until a
  // departure other than the airport is chosen, as before).
  const effectiveDirection = effectiveTransfer ? effectiveTransfer.direction
    : (dialogFrom && dialogFrom !== PHUKET_AIRPORT_ZONE_ID ? routeDirection(dialogFrom, dialogTo || PHUKET_AIRPORT_ZONE_ID) : 'from_airport');
  const dialogFromAirport = effectiveDirection === 'from_airport';
  const isZoneToZone = effectiveDirection === DIRECTION_ZONE_TO_ZONE;
  const fromChoices = departureChoices(transferRoutes);
  const toChoices = destinationChoices(transferRoutes, dialogFrom);
  const krabiPrices = ((((service?.details || {}).transport || {}).route_prices || {}).krabi) || {};
  const krabiTiers = KRABI_TIERS.filter((k) => Number(krabiPrices[k]) > 0);
  const airportLabel = t('transferBooking.airport');
  const effectiveRouteText = effectiveTransfer
    ? `${zoneLabel(effectiveFromZoneId, zoneById[effectiveFromZoneId]?.name, airportLabel)} → ${zoneLabel(effectiveToZoneId, zoneById[effectiveToZoneId]?.name, airportLabel)}`
    : null;
  // Pickup information (airport transfers): checked like the server does. No
  // phone is asked: traveller and provider talk through Ridalo's messages
  // (no WhatsApp for airport transfers, excursions and private drivers).
  const [luggage, setLuggage] = useState('');
  const [placeText, setPlaceText] = useState('');      // hotel / villa / address (drop-off or pick-up per direction)
  const [dropoffText, setDropoffText] = useState('');  // zone → zone: exact destination address
  const [flightNumber, setFlightNumber] = useState('');
  const [flightArrival, setFlightArrival] = useState('');
  // Hotel → airport only: room number (or "not known yet") and the hotel's Google Maps link.
  const [transferRoom, setTransferRoom] = useState('');
  const [transferRoomUnknown, setTransferRoomUnknown] = useState(false);
  const [hotelMapsUrl, setHotelMapsUrl] = useState('');
  const [pickupErrors, setPickupErrors] = useState({});
  // Provider's minimum notice for the selected direction (checked again by the server).
  const transferDirection = hasAirportZones ? effectiveDirection : null;
  const noticeHours = hasAirportZones ? minNoticeHours(service, transferDirection) : null;
  // Airport → hotel: the date picked is the LANDING date; the pickup is
  // computed (landing + 45 min, Phuket time), the server computes it again.
  const isArrival = transferDirection === 'from_airport';
  const arrivalPickup = isArrival ? pickupAfterLanding(bookingDate, flightArrival) : null;
  const pickupDate = isArrival ? arrivalPickup?.date : bookingDate;
  const pickupTime = isArrival ? arrivalPickup?.time : bookingTime;
  const noticeMissing = minNoticeViolation({ date: pickupDate, time: pickupTime, hours: noticeHours });
  const dayLabel = (ymd) => (ymd ? format(new Date(`${ymd}T00:00:00`), 'd MMM yyyy', { locale: dateLocale }) : '');
  const noticeBlock = noticeMissing ? (
    <p className="text-xs text-red-600 mt-1.5 font-medium" role="alert" data-testid="booking-error-minNotice">
      {t('transferBooking.errors.minNotice', { count: noticeMissing })}
    </p>
  ) : noticeHours ? (
    <p className="text-[11px] text-ink-muted mt-1.5" data-testid="booking-notice-hint">
      {t('transferBooking.form.noticeHint', { count: noticeHours })}
    </p>
  ) : null;
  const pickupErr = (field) => (pickupErrors[field]
    ? <p className="text-xs text-red-600 mt-1" data-testid={`booking-error-${field}`}>{t(`transferBooking.errors.${pickupErrors[field]}`, { max: capacity.luggageMax ?? MAX_LUGGAGE, count: capacity.luggageMax })}</p>
    : null);

  // Draft persistence — restore any pending Transport booking that a guest
  // submitted before we bounced them to login/signup. Mirrors ScooterDetail.js.
  const draftKey = serviceId ? `ridalo:transferBookingDraft:${serviceId}` : null;

  // Excursion V1 — set a sensible default departure time once the service loads
  // (first configured slot, else "09:00").
  useEffect(() => {
    if (!isExcursion) return;
    if (!departureTime) {
      setDepartureTime(excursionDepartureTimes[0] || '09:00');
    }
  }, [isExcursion, excursionDepartureTimes.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = () => {
    if (!draftKey) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        bookingDate, bookingTime, passengers, bookingDetails,
        fromZoneId, toZoneId, dialogFrom, dialogTo,
        luggage, placeText, dropoffText, flightNumber, flightArrival,
        transferRoom, transferRoomUnknown, hotelMapsUrl,
        selectedDuration, // Private Driver V1
        adults, children, infants, departureTime, pickupZone, hotelName, roomNumber, // Excursions
      }));
    } catch { /* quota / private mode — ignore */ }
  };

  const clearDraft = () => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!serviceId || !(isTransfer || isPrivateDriver || isExcursion)) return;
    const key = `ridalo:transferBookingDraft:${serviceId}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.bookingDate) setBookingDate(d.bookingDate);
      if (d.bookingTime) setBookingTime(d.bookingTime);
      if (typeof d.passengers === 'number') setPassengers(d.passengers);
      if (d.bookingDetails) setBookingDetails(d.bookingDetails);
      if (d.fromZoneId) setFromZoneId(d.fromZoneId);
      if (d.toZoneId) setToZoneId(d.toZoneId);
      if (d.dialogFrom) setDialogFrom(d.dialogFrom);
      if (d.dialogTo) setDialogTo(d.dialogTo);
      if (!d.dialogFrom && d.dialogZoneId) {      // draft saved before departure / destination existed
        setDialogFrom(d.dialogFromAirport === false ? d.dialogZoneId : PHUKET_AIRPORT_ZONE_ID);
        setDialogTo(d.dialogFromAirport === false ? PHUKET_AIRPORT_ZONE_ID : d.dialogZoneId);
      }
      if (d.dropoffText) setDropoffText(d.dropoffText);
      if (d.luggage !== undefined) setLuggage(d.luggage);
      if (d.placeText) setPlaceText(d.placeText);
      if (d.flightNumber) setFlightNumber(d.flightNumber);
      if (d.flightArrival) setFlightArrival(d.flightArrival);
      if (d.transferRoom) setTransferRoom(d.transferRoom);
      if (typeof d.transferRoomUnknown === 'boolean') setTransferRoomUnknown(d.transferRoomUnknown);
      if (d.hotelMapsUrl) setHotelMapsUrl(d.hotelMapsUrl);
      if (typeof d.selectedDuration === 'number') setSelectedDuration(d.selectedDuration);
      if (isExcursion) {
        if (typeof d.adults === 'number' && d.adults >= 1) setAdults(d.adults);
        if (typeof d.children === 'number') setChildren(d.children);
        if (typeof d.infants === 'number') setInfants(d.infants);
        if (d.departureTime) setDepartureTime(d.departureTime);
        if (d.pickupZone) setPickupZone(d.pickupZone);
        if (d.hotelName) setHotelName(d.hotelName);
        if (d.roomNumber) setRoomNumber(d.roomNumber);
      }
      // Re-open the booking dialog once the client is authenticated so they
      // can confirm right away. Guests keep it closed to avoid a loop.
      if (user?.role === 'client') setDialogOpen(true);
    } catch { /* ignore corrupt draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, isTransfer, isPrivateDriver, isExcursion, user?.role]);

  const handleBooking = async () => {
    if (!user) {
      // Guest — persist draft and open the auth prompt (login/signup).
      saveDraft();
      setDialogOpen(false);
      setAuthPromptOpen(true);
      return;
    }

    // Travellers and providers can book (a provider books other providers'
    // services as a traveller, with the same account) — never their own listing.
    const caps = Array.isArray(user.capabilities) ? user.capabilities : [];
    if (!['client', 'provider'].some((r) => user.role === r || caps.includes(r))) {
      toast.error(t('serviceDetail.toasts.clientOnly'));
      return;
    }
    if (service?.provider_id && service.provider_id === user.user_id) {
      toast.error(t('serviceDetail.toasts.ownListing'));
      return;
    }
    if (paxError) {
      toast.error(t(`serviceDetail.booking.${paxError}`, { count: capacity.passengersMax }));
      return;
    }

    if (!bookingDate) {
      toast.error(t('serviceDetail.toasts.pickDate'));
      return;
    }

    setBookingLoading(true);
    try {
      const isTransport = ['transfer', 'private_driver'].includes(service?.category);
      const payload = {
        service_id: serviceId,
        booking_date: bookingDate,
        details: { notes: bookingDetails, passengers },
      };
      if (isExcursion) {
        // Excursion V1 — server recomputes total from service.details.excursion.pricing.
        if (!departureTime) {
          toast.error(t('serviceDetail.toasts.pickDeparture'));
          setBookingLoading(false);
          return;
        }
        if (adults < 1) {
          toast.error(t('serviceDetail.toasts.oneAdultMin'));
          setBookingLoading(false);
          return;
        }
        if (excursionPickupAvailable && excursionPickupZones.length > 0 && !pickupZone) {
          toast.error(t('serviceDetail.toasts.pickPickupZone'));
          setBookingLoading(false);
          return;
        }
        if (excursionHotelPickup && !hotelName.trim()) {
          toast.error(t('serviceDetail.booking.hotelRequired'));
          setBookingLoading(false);
          return;
        }
        if (excursionPricing.price_by_zone && !pickupZone) {
          toast.error(t('serviceDetail.toasts.pickPickupZoneByZone'));
          setBookingLoading(false);
          return;
        }
        payload.start_time = departureTime;
        payload.end_time = departureTime;
        payload.end_date = bookingDate;
        payload.details = {
          notes: bookingDetails,
          adults,
          children,
          infants,
          departure_time: departureTime,
          pickup_zone: pickupZone || null,
          ...(excursionHotelPickup ? { hotel_name: hotelName.trim(), room_number: roomNumber.trim() || null } : {}),
          matched_total: excursionTotal,
        };
      } else if (isTransport) {
        payload.end_date = bookingDate;
        payload.start_time = bookingTime;
        payload.end_time = bookingTime;
        // For Airport Transfers, we ALWAYS attach the airport route the traveler
        // is booking (either from the marketplace URL, or picked in the booking
        // dialog). Server-side, /api/bookings recomputes the price from the
        // service's airport_transfers[] for that zone.
        if (isTransfer) {
          if (!effectiveFromZoneId || !effectiveToZoneId) {
            toast.error(t('serviceDetail.toasts.pickDestZone'));
            setBookingLoading(false);
            return;
          }
          const dir = routeDirection(effectiveFromZoneId, effectiveToZoneId);
          payload.details = {
            ...payload.details,
            from_zone_id: effectiveFromZoneId,
            to_zone_id: effectiveToZoneId,
            direction: dir,
          };
          if (hasAirportZones) {
            const missing = minNoticeViolation({ date: pickupDate, time: pickupTime, hours: noticeHours });
            if (missing) {
              toast.error(t('transferBooking.errors.minNotice', { count: missing }));
              setBookingLoading(false);
              return;
            }
            const errors = validatePickup({ direction: dir, luggage, place: placeText, flightNumber, flightArrival, luggageMax: capacity.luggageMax,
              room: transferRoom, roomUnknown: transferRoomUnknown, mapsUrl: hotelMapsUrl, dropoff: dropoffText });
            setPickupErrors(errors);
            if (Object.keys(errors).length) {
              toast.error(t('transferBooking.errors.fixForm'));
              setBookingLoading(false);
              return;
            }
            Object.assign(payload.details, {
              luggage: Number(luggage),
              ...(dir === 'from_airport'
                ? { dropoff_location: placeText.trim(), flight_number: normalizeFlight(flightNumber),
                    flight_arrival_date: bookingDate, flight_arrival_time: flightArrival }
                : dir === DIRECTION_ZONE_TO_ZONE
                ? { pickup_location: placeText.trim(), dropoff_location: dropoffText.trim() }
                : { pickup_location: placeText.trim(), ...(flightNumber.trim() ? { flight_number: normalizeFlight(flightNumber) } : {}),
                    ...(transferRoomUnknown ? { room_number_unknown: true } : { room_number: transferRoom.trim() }),
                    hotel_maps_url: cleanMapsUrl(hotelMapsUrl) || hotelMapsUrl.trim() }),
            });
            if (dir === 'from_airport' && arrivalPickup) {
              // Pickup computed from the landing (the server recomputes it).
              payload.booking_date = arrivalPickup.date;
              payload.start_time = arrivalPickup.time;
              payload.end_time = arrivalPickup.time;
            }
          }
        }
        // Private Driver V1 — attach duration_hours and matched_price.
        // Server will recompute the price server-side from service.durations[].
        if (isPrivateDriver) {
          if (!selectedDurationObj) {
            toast.error(t('serviceDetail.toasts.pickDuration'));
            setBookingLoading(false);
            return;
          }
          payload.details = {
            ...payload.details,
            duration_hours: selectedDuration,
            matched_price: privateDriverPrice,
          };
        }
      }
      const { data } = await axios.post(
        `${API}/bookings`,
        payload,
        { withCredentials: true }
      );
      // Only show success if the backend actually returned a booking reference.
      if (!data || !data.booking_id) {
        throw new Error('no booking reference');
      }
      // The traveller stays on the listing and sees the request's reference
      // and next step (the provider still has to accept).
      setCreatedBookingId(data.booking_id);
      clearDraft();
      setDialogOpen(false);
      setConfirmOpen(true);
    } catch (error) {
      toast.error(translateServerError(error.response?.data?.detail) || t('serviceDetail.toasts.bookingError'));
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="service-detail-page">
      <SEO
        title={`${service.title}${service.location ? ` — ${service.location}` : ''} | Ridalo`}
        description={(() => {
          const catLabel = t(`seo.serviceDetail.cat.${['excursion', 'transfer', 'private_driver'].includes(service.category) ? service.category : 'other'}`);
          const parts = [];
          parts.push(service.location
            ? t('seo.serviceDetail.lineLocation', { category: catLabel, title: service.title, location: service.location })
            : t('seo.serviceDetail.line', { category: catLabel, title: service.title }));
          const seoOffer = listingOffer(service);
          if (seoOffer) parts.push(t('seo.serviceDetail.fromPrice', { price: Number(seoOffer.low).toLocaleString(numberLocale()) }));
          if (businessNameOf(service)) {
            parts.push(t('seo.serviceDetail.provider', { name: businessNameOf(service) }));
          }
          parts.push(t('seo.serviceDetail.bookOnline'));
          return parts.join(' ').slice(0, 300);
        })()}
        canonical={`https://ridalo.co/services/${serviceId}`}
      />
      <Header />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button — sends the traveler back to where they came from
              (usually the Transport listing). Falls back to /transport when the
              history stack is empty. */}
          {['transfer', 'private_driver', 'excursion'].includes(service.category) && (
            <button
              type="button"
              onClick={() => {
                // Preserve Private Driver tab when returning to marketplace.
                // Airport Transfer uses default tab (transfer) or no explicit tab.
                if (isPrivateDriver) {
                  navigate('/transport?tab=private_driver');
                } else if (isExcursion) {
                  navigate('/services?category=excursion');
                } else if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/transport');
                }
              }}
              className="inline-flex items-center gap-1.5 text-sm text-primary/80 hover:text-primary mb-4 transition-colors max-sm:min-h-11"
              data-testid="service-back-button"
            >
              <ArrowLeft className="w-4 h-4" /> {t('serviceDetail.back')}
            </button>
          )}

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-ink-light mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent transition-colors">{t('seo.breadcrumb.home')}</Link>
            <span>/</span>
            <Link
              to={isTransfer ? '/transport' : isExcursion ? '/services?category=excursion' : '/services'}
              className="hover:text-accent transition-colors"
            >
              {isTransfer ? t('seo.breadcrumb.transport') : isExcursion ? t('seo.breadcrumb.excursions') : t('seo.breadcrumb.services')}
            </Link>
            <span>/</span>
            <span className="text-primary font-medium">{service?.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Images */}
              {service.images && service.images.length > 0 && (isExcursion ? (
                // Excursions: same layout; every photo opens the full-screen gallery on that photo.
                <div className="mb-8">
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(0)}
                    className="block w-full rounded-2xl overflow-hidden cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    aria-label={t('serviceDetail.gallery.open', { n: 1, total: service.images.length })}
                    data-testid="gallery-open-0"
                  >
                    <img
                      src={service.images[0]}
                      alt={pickTranslation(service, 'title', i18n.language) || service.title}
                      className="w-full h-96 object-cover rounded-2xl"
                      data-testid="service-main-image"
                    />
                  </button>
                  {service.images.length > 1 && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {service.images.slice(1, 4).map((img, index) => {
                        const hidden = index === 2 ? service.images.length - 4 : 0;   // photos not shown as thumbnails
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setLightboxIndex(index + 1)}
                            className="relative block rounded-lg overflow-hidden cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            aria-label={t('serviceDetail.gallery.open', { n: index + 2, total: service.images.length })}
                            data-testid={`gallery-open-${index + 1}`}
                          >
                            <img
                              src={img}
                              alt={`${pickTranslation(service, 'title', i18n.language) || service.title} ${index + 2}`}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            {hidden > 0 && (
                              <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white text-lg font-semibold" data-testid="gallery-more">
                                +{hidden}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <PhotoLightbox
                    images={service.images}
                    open={lightboxIndex !== null}
                    startIndex={lightboxIndex ?? 0}
                    onOpenChange={(o) => { if (!o) setLightboxIndex(null); }}
                    alt={pickTranslation(service, 'title', i18n.language) || service.title}
                  />
                </div>
              ) : (
                <div className="mb-8">
                  <img
                    src={service.images[0]}
                    alt={pickTranslation(service, 'title', i18n.language) || service.title}
                    className="w-full h-96 object-cover rounded-2xl"
                    data-testid="service-main-image"
                  />
                  {service.images.length > 1 && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {service.images.slice(1, 4).map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`${pickTranslation(service, 'title', i18n.language) || service.title} ${index + 2}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Info */}
              <div className="card-dashboard mb-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                  <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-primary" data-testid="service-title">
                    {pickTranslation(service, 'title', i18n.language) || service.title}
                  </h1>
                  <div className="shrink-0">
                    <ShareListingButton category={service.category} listingId={service.service_id}
                                        title={pickTranslation(service, 'title', i18n.language) || service.title} />
                  </div>
                </div>
                {!isTransfer && (
                  <div className="flex items-center text-ink-light mb-6">
                    <MapPin className="w-5 h-5 mr-2" />
                    <span data-testid="service-location">{service.location}</span>
                  </div>
                )}
                {isTransfer && (
                  <div className="flex items-center text-ink-light mb-6" data-testid="service-airport-line">
                    <MapPin className="w-5 h-5 mr-2" />
                    <span>{t('serviceDetail.airportLine')}</span>
                  </div>
                )}
                {service.description && (
                  <p className="text-base leading-relaxed text-ink-light mb-6" data-testid="service-description">
                    {pickTranslation(service, 'description', i18n.language) || service.description}
                  </p>
                )}
                {isTransfer && (() => {
                  const v = ((service.details || {}).transport || {}).vehicle || {};
                  const parts = [v.vehicle_type_label || v.vehicle_type, v.brand, v.model].filter(Boolean).join(' · ');
                  const caps = [];
                  if (v.passengers_max) caps.push(t('serviceDetail.vehiclePax', { count: v.passengers_max }));
                  if (v.luggage_max != null) caps.push(t('serviceDetail.vehicleLuggage', { count: v.luggage_max }));
                  if (!parts && caps.length === 0) return null;
                  return (
                    <div className="border-t border-slate-100 pt-4 mt-2" data-testid="service-vehicle-info">
                      <p className="text-sm font-semibold text-primary mb-1">{t('serviceDetail.vehicleSection')}</p>
                      <p className="text-sm text-ink-light">{parts}</p>
                      {caps.length > 0 && <p className="text-xs text-ink-muted mt-1">{caps.join(' · ')}</p>}
                    </div>
                  );
                })()}
              </div>

              {/* Airport Transfers price grid — one card per served zone */}
              {isTransfer && airportTransfers.length > 0 && (
                <div className="card-dashboard mb-8" data-testid="transfer-prices-section">
                  <div className="flex items-center mb-4">
                    <MapPin className="w-5 h-5 mr-2 text-accent" />
                    <h2 className="text-xl font-medium text-primary">{t('serviceDetail.transferPricesTitle')}</h2>
                  </div>
                  <p className="text-xs text-ink-muted mb-4">{t('serviceDetail.transferPricesHint')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {airportTransfers.map((t) => {
                      const name = zoneById[t.zone_id]?.name || t.zone_id;
                      const isSel = destinationZoneId === t.zone_id || [dialogFrom, dialogTo].includes(t.zone_id);
                      return (
                        <div
                          key={t.zone_id}
                          className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition ${
                            isSel ? 'border-accent bg-accent/5' : 'border-slate-200 bg-white'
                          }`}
                          data-testid={`transfer-price-row-${t.zone_id}`}
                        >
                          <span className="text-sm font-medium text-primary">{name}</span>
                          <span className="text-sm font-semibold text-primary">
                            {Number(t.price).toLocaleString(numberLocale())} <span className="text-xs text-ink-muted font-normal">THB</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Phuket ↔ Krabi prices set by the provider (Krabi Town and Ao Nang share each price, both ways). */}
              {isTransfer && krabiTiers.length > 0 && (
                <div className="card-dashboard mb-8" data-testid="krabi-prices-section">
                  <div className="flex items-center mb-4">
                    <MapPin className="w-5 h-5 mr-2 text-accent" />
                    <h2 className="text-xl font-medium text-primary">{t('krabiTransfers.publicTitle')}</h2>
                  </div>
                  <p className="text-xs text-ink-muted mb-4">{t('krabiTransfers.publicHint')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {krabiTiers.map((k) => (
                      <div key={k} className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 border-slate-200 bg-white" data-testid={`krabi-price-row-${k}`}>
                        <span className="text-sm font-medium text-primary">{t(`krabiTransfers.publicRows.${k}`)}</span>
                        <span className="text-sm font-semibold text-primary whitespace-nowrap">
                          {Number(krabiPrices[k]).toLocaleString(numberLocale())} <span className="text-xs text-ink-muted font-normal">THB</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Excursion V1 — dedicated info section (duration, itinerary, included, restrictions) */}
              {isExcursion && (
                <div className="card-dashboard mb-8" data-testid="excursion-info-section">
                  <div className="flex items-center mb-4">
                    <Info className="w-5 h-5 mr-2 text-accent" />
                    <h2 className="text-xl font-medium text-primary">{t('serviceDetail.excursion.sectionTitle')}</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    {excursionCfg.destination && (
                      <div>
                        <p className="text-xs uppercase text-ink-muted mb-0.5">{t('serviceDetail.excursion.destination')}</p>
                        <p className="font-medium text-primary" data-testid="excursion-destination-value">{excursionCfg.destination}</p>
                      </div>
                    )}
                    {excursionCfg.duration_hours && (
                      <div>
                        <p className="text-xs uppercase text-ink-muted mb-0.5">{t('serviceDetail.excursion.duration')}</p>
                        <p className="font-medium text-primary" data-testid="excursion-duration-value">{t('serviceDetail.excursion.durationValue', { h: excursionCfg.duration_hours })}</p>
                      </div>
                    )}
                    {excursionMaxParticipants && (
                      <div>
                        <p className="text-xs uppercase text-ink-muted mb-0.5">{t('serviceDetail.excursion.maxParticipants')}</p>
                        <p className="font-medium text-primary">{excursionMaxParticipants}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs uppercase text-ink-muted mb-0.5">{t('serviceDetail.excursion.prices')}</p>
                      <p className="font-medium text-primary">
                        {excursionAdultPrice != null && <>{t('serviceDetail.excursion.adultPrice', { price: Number(excursionAdultPrice).toLocaleString(numberLocale()) })}</>}
                        {excursionChildPrice != null && excursionAdultPrice != null && <> · {excursionChildPriceSet
                          ? t('serviceDetail.excursion.childPrice', { price: Number(excursionChildPrice).toLocaleString(numberLocale()) })
                          : t('serviceDetail.excursion.childAdultPrice')}</>}
                      </p>
                    </div>
                  </div>
                  {excursionCfg.itinerary && (
                    <div className="border-t border-slate-100 pt-4 mt-2" data-testid="excursion-itinerary-section">
                      <p className="text-sm font-semibold text-primary mb-1">{t('serviceDetail.excursion.itinerary')}</p>
                      <p className="text-sm text-ink-light whitespace-pre-line">{excursionCfg.itinerary}</p>
                    </div>
                  )}
                  {excursionCfg.included && (
                    <div className="border-t border-slate-100 pt-4 mt-4" data-testid="excursion-included-section">
                      <p className="text-sm font-semibold text-emerald-700 mb-1">{t('serviceDetail.excursion.included')}</p>
                      <p className="text-sm text-ink-light whitespace-pre-line">{excursionCfg.included}</p>
                    </div>
                  )}
                  {excursionCfg.not_included && (
                    <div className="border-t border-slate-100 pt-4 mt-4" data-testid="excursion-not-included-section">
                      <p className="text-sm font-semibold text-red-700 mb-1">{t('serviceDetail.excursion.notIncluded')}</p>
                      <p className="text-sm text-ink-light whitespace-pre-line">{excursionCfg.not_included}</p>
                    </div>
                  )}
                  {excursionCfg.restrictions && (
                    <div className="border-t border-slate-100 pt-4 mt-4" data-testid="excursion-restrictions-section">
                      <p className="text-sm font-semibold text-primary mb-1">{t('serviceDetail.excursion.important')}</p>
                      <p className="text-sm text-ink-light whitespace-pre-line">{excursionCfg.restrictions}</p>
                    </div>
                  )}
                  {excursionCfg.programs && excursionCfg.programs.length > 0 && (
                    <div className="border-t border-slate-100 pt-4 mt-4" data-testid="excursion-programs-section">
                      <p className="text-sm font-semibold text-primary mb-2">{t('serviceDetail.excursion.programs')}</p>
                      <div className="space-y-2">
                        {excursionCfg.programs.map((p, i) => (
                          <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                            <div className="flex items-baseline justify-between">
                              <span className="font-medium text-primary">{p.name}</span>
                              <span className="text-xs text-ink-muted">{t('serviceDetail.excursion.adultPrice', { price: Number(p.adult_price).toLocaleString(numberLocale()) })}{p.child_price != null && <> · {t('serviceDetail.excursion.childPrice', { price: Number(p.child_price).toLocaleString(numberLocale()) })}</>}</span>
                            </div>
                            {p.description && <p className="text-xs text-ink-light mt-0.5">{p.description}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Conditions — hidden for airport transfers (deposit info shown on the sidebar) */}
              {!isTransfer && !isExcursion && (
                <div className="card-dashboard">
                  <div className="flex items-center mb-4">
                    <Info className="w-5 h-5 mr-2 text-accent" />
                    <h2 className="text-xl font-medium text-primary">{t('serviceDetail.conditionsTitle')}</h2>
                  </div>
                  <p className="text-base leading-relaxed text-ink-light" data-testid="service-conditions">
                    {pickTranslation(service, 'conditions', i18n.language) || service.conditions}
                  </p>
                </div>
              )}

              {/* Travellers' reviews (airport transfers, private drivers) */}
              {isTransportListing && <ServiceReviewsSection serviceId={service.service_id} />}
            </div>

            {/* Booking Card */}
            <div className="lg:col-span-1">
              <div className="card-dashboard sticky top-24">
                {displayRoute && (
                  <div className="mb-4 px-3 py-2 bg-accent/10 border border-accent/30 rounded-xl" data-testid="airport-route-summary">
                    <p className="text-xs text-ink-muted mb-0.5">{t('serviceDetail.sidebar.routeSelected')}</p>
                    <p className="text-sm font-semibold text-primary">✈️ {displayRoute.from} → {displayRoute.to}</p>
                  </div>
                )}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm text-ink-light">
                    {(isStartingFrom || isPrivateDriverStartingFrom) ? t('serviceDetail.sidebar.startingFrom') : (isExcursion ? t('serviceDetail.sidebar.adultFrom') : t('serviceDetail.sidebar.price'))}
                  </span>
                  <div className="flex items-baseline text-3xl font-semibold text-primary" data-testid="service-price">
                    {Number(isExcursion ? (excursionAdultPrice ?? service?.price ?? 0) : displayPrice).toLocaleString(numberLocale())}
                    <span className="ml-2 text-base font-medium text-ink-light">THB</span>
                  </div>
                </div>
                {isExcursion && excursionTotal != null && (
                  <div className="mb-4 flex items-baseline justify-between rounded-xl bg-accent/5 border border-accent/20 px-3 py-2" data-testid="excursion-total-preview">
                    <span className="text-xs uppercase tracking-wide text-ink-muted">{t('serviceDetail.sidebar.estimatedTotal')}</span>
                    <span className="text-lg font-semibold text-primary">
                      {Number(excursionTotal).toLocaleString(numberLocale())} THB
                    </span>
                  </div>
                )}
                {isExcursion && (
                  <div className="mb-4 text-xs text-ink-muted" data-testid="service-deposit-info">
                    {excursionPaymentPct < 100
                      ? t('serviceDetail.sidebar.excursionDeposit', { pct: excursionPaymentPct })
                      : t('serviceDetail.sidebar.excursionFullPayment')}
                  </div>
                )}
                {(isTransfer || isPrivateDriver) && (
                  <div className="mb-4 text-xs text-ink-muted" data-testid="service-deposit-info">
                    {transportDepositPct > 0
                      ? t('serviceDetail.sidebar.depositRequired', { pct: transportDepositPct })
                      : t('serviceDetail.sidebar.noDeposit')}
                  </div>
                )}

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary w-full mb-4" data-testid="open-booking-button">
                      {t('serviceDetail.sidebar.openBooking')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent
                    className="max-h-[85vh] overflow-y-auto"
                    data-testid="booking-dialog"
                    // Escape first closes the excursion date picker, not the whole booking window.
                    onEscapeKeyDown={(e) => { if (excursionCalendarOpen) { e.preventDefault(); setExcursionCalendarOpen(false); } }}
                  >
                    <DialogHeader>
                      <DialogTitle>{t('serviceDetail.booking.title', { name: pickTranslation(service, 'title', i18n.language) || service.title })}</DialogTitle>
                      <DialogDescription className="text-sm text-ink-muted">
                        {t('serviceDetail.booking.subtitle')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5 mt-4">
                      <div>
                        <Label htmlFor="booking-date" className="text-sm font-semibold text-primary mb-2 block">
                          {isArrival ? t('transferBooking.form.landingDate') : t('serviceDetail.booking.dateLabel')}
                        </Label>
                        {isExcursion ? (
                          // Excursions: the calendar opens INSIDE the booking window (no
                          // floating layer outside the modal window, whose focus trap,
                          // scroll lock and pointer blocking could make the dates
                          // unreachable on phones). Past days and days the provider
                          // blocked are disabled; the server checks them again.
                          <div ref={excursionCalendarRef}>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setExcursionCalendarOpen((o) => !o)}
                              aria-expanded={excursionCalendarOpen}
                              className="w-full justify-start text-left font-normal input-field h-11"
                              data-testid="booking-date-input"
                            >
                              <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                              {bookingDate
                                ? format(new Date(`${bookingDate}T00:00:00`), 'EEEE d MMMM yyyy', { locale: dateLocale })
                                : t('serviceDetail.booking.datePlaceholder')}
                            </Button>
                            {excursionCalendarOpen && (excursionHasOpenDay ? (
                              <div className="mt-2 flex justify-center" data-testid="booking-date-calendar-panel">
                                <CalendarCmp
                                  mode="single"
                                  locale={dateLocale}
                                  selected={bookingDate ? new Date(`${bookingDate}T00:00:00`) : undefined}
                                  defaultMonth={bookingDate ? new Date(`${bookingDate}T00:00:00`) : excursionToday}
                                  onSelect={(d) => {
                                    if (!d) return;
                                    setBookingDate(localYmd(d));
                                    setExcursionCalendarOpen(false);
                                  }}
                                  disabled={[{ before: excursionToday }, (d) => excursionBlockedDays.has(localYmd(d))]}
                                  data-testid="booking-date-calendar"
                                  className="rounded-xl border bg-white"
                                />
                              </div>
                            ) : (
                              <p className="mt-2 text-sm text-red-600 font-medium" role="alert" data-testid="booking-no-dates">
                                {t('serviceDetail.booking.noDatesAvailable')}
                              </p>
                            ))}
                          </div>
                        ) : ['transfer', 'private_driver'].includes(service.category) ? (
                          <Popover modal={false}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start text-left font-normal input-field h-11"
                                data-testid="booking-date-input"
                              >
                                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                                {bookingDate
                                  ? format(new Date(`${bookingDate}T00:00:00`), 'EEEE d MMMM yyyy', { locale: dateLocale })
                                  : t('serviceDetail.booking.datePlaceholder')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={8} avoidCollisions={true}>
                              <CalendarCmp
                                mode="single"
                                locale={dateLocale}
                                selected={bookingDate ? new Date(`${bookingDate}T00:00:00`) : undefined}
                                onSelect={(d) => {
                                  if (!d) return;
                                  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                                  setBookingDate(ymd);
                                }}
                                disabled={{ before: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Bangkok' })) }}
                                initialFocus
                                data-testid="booking-date-calendar"
                                className="rounded-xl border shadow-lg bg-white"
                              />
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <Input
                              id="booking-date"
                              type="date"
                              value={bookingDate}
                              onChange={(e) => setBookingDate(e.target.value)}
                              className="input-field pl-10"
                              min={new Date().toISOString().split('T')[0]}
                              data-testid="booking-date-input"
                            />
                          </div>
                        )}
                      </div>
                      {isExcursion && (
                        <>
                          <div>
                            <Label htmlFor="excursion-departure" className="text-sm font-semibold text-primary mb-2 block">
                              {t('serviceDetail.booking.departureLabel')}
                            </Label>
                            {excursionDepartureTimes.length > 0 ? (
                              <div className="relative">
                                <select
                                  id="excursion-departure"
                                  value={departureTime}
                                  onChange={(e) => setDepartureTime(e.target.value)}
                                  className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm text-primary focus:border-accent focus:outline-none h-11"
                                  data-testid="excursion-departure-select"
                                >
                                  <option value="">{t('serviceDetail.booking.chooseTime')}</option>
                                  {excursionDepartureTimes.map((t2) => (
                                    <option key={t2} value={t2} data-testid={`excursion-departure-${t2}`}>{t2}</option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                              </div>
                            ) : (
                              <Input
                                id="excursion-departure"
                                type="time"
                                value={departureTime}
                                onChange={(e) => setDepartureTime(e.target.value)}
                                className="input-field h-11"
                                data-testid="excursion-departure-input"
                              />
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs font-semibold text-primary mb-1 block">{t('serviceDetail.booking.adults')}</Label>
                              <Input
                                type="number" min="1" max="30"
                                value={adults}
                                onChange={(e) => setAdults(Math.max(1, parseInt(e.target.value) || 1))}
                                className="input-field h-11"
                                data-testid="excursion-adults-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-semibold text-primary mb-1 block">{ageLabels.children}</Label>
                              <Input
                                type="number" min="0" max="30"
                                value={children}
                                onChange={(e) => setChildren(Math.max(0, parseInt(e.target.value) || 0))}
                                className="input-field h-11"
                                data-testid="excursion-children-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-semibold text-primary mb-1 block">{ageLabels.infants}</Label>
                              <Input
                                type="number" min="0" max="10"
                                value={infants}
                                onChange={(e) => setInfants(Math.max(0, parseInt(e.target.value) || 0))}
                                className="input-field h-11"
                                data-testid="excursion-infants-input"
                              />
                            </div>
                          </div>
                          <p className="text-[11px] text-ink-muted -mt-3">{ageLabels.note}</p>
                          {(excursionPickupAvailable && excursionPickupZones.length > 0) || excursionPricing.price_by_zone ? (
                            <div>
                              <Label htmlFor="excursion-pickup-zone" className="text-sm font-semibold text-primary mb-2 block">
                                {t('serviceDetail.booking.pickupZone')} {excursionPricing.price_by_zone && <span className="text-xs text-ink-muted">{t('serviceDetail.booking.pickupZoneByZone')}</span>}
                              </Label>
                              <div className="relative">
                                <select
                                  id="excursion-pickup-zone"
                                  value={pickupZone}
                                  onChange={(e) => setPickupZone(e.target.value)}
                                  className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm text-primary focus:border-accent focus:outline-none h-11"
                                  data-testid="excursion-pickup-zone-select"
                                >
                                  <option value="">{t('serviceDetail.booking.chooseZone')}</option>
                                  {(excursionPricing.price_by_zone
                                    ? (excursionPricing.zone_prices || []).map((zp) => zp.zone)
                                    : excursionPickupZones
                                  ).map((z) => (
                                    <option key={z} value={z}>{z}</option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                              </div>
                            </div>
                          ) : null}
                          {excursionHotelPickup && (
                            <div className="space-y-3" data-testid="excursion-hotel-pickup">
                              <div>
                                <Label htmlFor="excursion-hotel-name" className="text-sm font-semibold text-primary mb-2 block">
                                  {t('serviceDetail.booking.hotelName')} *
                                </Label>
                                <Input
                                  id="excursion-hotel-name"
                                  value={hotelName}
                                  maxLength={120}
                                  onChange={(e) => setHotelName(e.target.value)}
                                  placeholder={t('serviceDetail.booking.hotelNamePlaceholder')}
                                  className="input-field h-11"
                                  data-testid="excursion-hotel-name-input"
                                />
                              </div>
                              <div>
                                <Label htmlFor="excursion-room-number" className="text-sm font-semibold text-primary mb-2 block">
                                  {t('serviceDetail.booking.roomNumber')}
                                </Label>
                                <Input
                                  id="excursion-room-number"
                                  value={roomNumber}
                                  maxLength={20}
                                  onChange={(e) => setRoomNumber(e.target.value)}
                                  placeholder={t('serviceDetail.booking.roomNumberPlaceholder')}
                                  className="input-field h-11"
                                  data-testid="excursion-room-number-input"
                                />
                                <p className="text-[11px] text-ink-muted mt-1">{t('serviceDetail.booking.roomNumberHelp')}</p>
                              </div>
                              <PickupInfoNotice testId="excursion-pickup-notice" />
                            </div>
                          )}
                          {excursionTotal != null && (
                            <div className="rounded-xl bg-accent/5 border border-accent/20 p-3 space-y-1.5" data-testid="excursion-price-summary">
                              <p className="text-xs font-semibold uppercase text-primary">{t('serviceDetail.booking.priceDetails')}</p>
                              <div className="flex justify-between text-sm">
                                <span className="text-ink-light">{t('serviceDetail.booking.adultsLine', { count: adults, price: Number(excursionAdultPrice ?? 0).toLocaleString(numberLocale()) })}</span>
                                <span className="font-medium text-primary">{Number(adults * (excursionAdultPrice ?? 0)).toLocaleString(numberLocale())} THB</span>
                              </div>
                              {children > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-ink-light">{t(excursionChildPriceSet ? 'serviceDetail.booking.childrenLine' : 'serviceDetail.booking.childrenLineAdultPrice', { count: children, price: Number(excursionChildPrice ?? excursionAdultPrice ?? 0).toLocaleString(numberLocale()) })}</span>
                                  <span className="font-medium text-primary">{Number(children * (excursionChildPrice ?? excursionAdultPrice ?? 0)).toLocaleString(numberLocale())} THB</span>
                                </div>
                              )}
                              {infants > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-ink-light">{ageLabels.infantsLine(infants)}</span>
                                  <span className="font-medium text-primary">0 THB</span>
                                </div>
                              )}
                              <div className="flex justify-between border-t border-accent/30 pt-1.5 mt-1">
                                <span className="text-sm font-semibold text-primary">{t('serviceDetail.booking.total')}</span>
                                <span className="text-lg font-bold text-primary" data-testid="excursion-total-value">
                                  {Number(excursionTotal).toLocaleString(numberLocale())} THB
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {['transfer', 'private_driver'].includes(service.category) && (
                        <>
                          {!isArrival && (
                          <div>
                            <Label htmlFor="booking-time" className="text-sm font-semibold text-primary mb-2 block">
                              {service.category === 'private_driver' ? t('serviceDetail.booking.startTime')
                                : transferDirection === 'to_airport' ? t('transferBooking.form.pickupTime')
                                : t('serviceDetail.booking.pickupTime')}
                            </Label>
                            <div className="relative">
                              <select
                                id="booking-time"
                                value={bookingTime}
                                onChange={(e) => setBookingTime(e.target.value)}
                                className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm text-primary focus:border-accent focus:outline-none h-11"
                                data-testid="booking-time-input"
                              >
                                <option value="">{t('serviceDetail.booking.chooseHour')}</option>
                                {TRANSPORT_HOUR_SLOTS.map((h) => {
                                  // Disable past slots when the selected booking date is TODAY
                                  // in Asia/Bangkok. Transport remains 24/7 for future dates.
                                  const disabled = (() => {
                                    if (!bookingDate) return false;
                                    const nowBK = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }));
                                    const todayBK = `${nowBK.getFullYear()}-${String(nowBK.getMonth() + 1).padStart(2, '0')}-${String(nowBK.getDate()).padStart(2, '0')}`;
                                    if (bookingDate !== todayBK) return false;
                                    const [hh, mm] = h.split(':').map(Number);
                                    return hh * 60 + mm <= nowBK.getHours() * 60 + nowBK.getMinutes();
                                  })();
                                  return (
                                    <option
                                      key={h}
                                      value={h}
                                      disabled={disabled}
                                      data-testid={`booking-time-slot-${h}`}
                                    >
                                      {h}{disabled ? t('serviceDetail.booking.pastSuffix') : ''}
                                    </option>
                                  );
                                })}
                              </select>
                              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            </div>
                            {hasAirportZones ? (
                              <>
                                {noticeBlock}
                                <p className="text-[11px] text-ink-muted mt-1">{t('transferBooking.form.manualConfirm')}</p>
                              </>
                            ) : (
                              <p className="text-[11px] text-ink-muted mt-1.5">{t('serviceDetail.booking.availabilityNote')}</p>
                            )}
                          </div>
                          )}
                          <div>
                            <Label htmlFor="booking-pax" className="text-sm font-semibold text-primary mb-2 block">
                              {t('serviceDetail.booking.passengers')}
                            </Label>
                            <Input
                              id="booking-pax"
                              type="number" min="1" max={capacity.passengersMax || 20}
                              value={passengers}
                              onChange={(e) => setPassengers(parseInt(e.target.value) || 1)}
                              className="input-field h-11"
                              data-testid="booking-pax-input"
                            />
                            {paxError ? (
                              <p className="text-xs text-red-600 mt-1 font-medium" role="alert" data-testid="booking-error-passengers">
                                {t(`serviceDetail.booking.${paxError}`, { count: capacity.passengersMax })}
                              </p>
                            ) : capacity.passengersMax ? (
                              <p className="text-[11px] text-ink-muted mt-1" data-testid="booking-pax-hint">
                                {t('serviceDetail.booking.paxCapacityHint', { count: capacity.passengersMax })}
                              </p>
                            ) : null}
                          </div>
                          {/* Private Driver V1 — duration selector (enabled durations only) */}
                          {isPrivateDriver && availableDurations.length > 0 && (
                            <div className="p-3 bg-accent/5 border border-accent/20 rounded-xl space-y-3" data-testid="booking-duration-picker">
                              <Label className="text-sm font-semibold text-primary block">{t('serviceDetail.booking.durationLabel')}</Label>
                              <div className="space-y-2">
                                {availableDurations.map((dur) => {
                                  const isSelected = selectedDuration === dur.duration_hours;
                                  const label = dur.duration_hours === 4 ? t('serviceDetail.booking.duration4h') : dur.duration_hours === 8 ? t('serviceDetail.booking.duration8h') : dur.duration_hours === 12 ? t('serviceDetail.booking.duration12h') : t('serviceDetail.booking.durationGeneric', { h: dur.duration_hours });
                                  return (
                                    <button
                                      key={dur.duration_hours}
                                      type="button"
                                      onClick={() => setSelectedDuration(dur.duration_hours)}
                                      className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-medium transition flex items-center justify-between ${
                                        isSelected
                                          ? 'border-accent bg-accent/10 text-primary'
                                          : 'border-slate-200 bg-white text-primary hover:border-accent/50'
                                      }`}
                                      data-testid={`booking-duration-${dur.duration_hours}`}
                                    >
                                      <span>{label}</span>
                                      <span className="font-semibold">{Number(dur.price).toLocaleString(numberLocale())} THB</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {hasAirportZones && (
                            <div className="p-3 bg-accent/5 border border-accent/20 rounded-xl space-y-3" data-testid="booking-zone-picker">
                              <Label className="text-sm font-semibold text-primary block">{t('serviceDetail.booking.route')}</Label>
                              {[
                                ['from', t('transferBooking.form.departure'), dialogFrom, (v) => {
                                  setDialogFrom(v);
                                  if (!findRoute(transferRoutes, v, dialogTo)) setDialogTo('');
                                }, fromChoices.map((id) => ({ id, label: zoneLabel(id, zoneById[id]?.name, airportLabel) }))],
                                ['to', t('transferBooking.form.destination'), dialogTo, setDialogTo, toChoices.map((r) => ({
                                  id: r.to_zone_id,
                                  label: `${zoneLabel(r.to_zone_id, zoneById[r.to_zone_id]?.name, airportLabel)} — ${Number(r.price).toLocaleString(numberLocale())} THB`,
                                }))],
                              ].map(([key, label, value, onChange, options]) => (
                                <div key={key}>
                                  <Label htmlFor={`booking-${key}-select`} className="text-xs font-medium text-ink-light mb-1 block">{label}</Label>
                                  <div className="relative">
                                    <select
                                      id={`booking-${key}-select`}
                                      value={value}
                                      onChange={(e) => onChange(e.target.value)}
                                      disabled={key === 'to' && !dialogFrom}
                                      className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm text-primary focus:border-accent focus:outline-none h-11 disabled:bg-slate-50"
                                      data-testid={`booking-${key}-select`}
                                    >
                                      <option value="">{t(key === 'from' ? 'transferBooking.form.chooseDeparture' : 'transferBooking.form.chooseDestination')}</option>
                                      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                  </div>
                                </div>
                              ))}
                              {effectivePrice != null && (
                                <div className="flex justify-between items-baseline pt-2 border-t border-accent/20">
                                  <span className="text-xs text-ink-muted">{t('serviceDetail.booking.pricePerTrip')}</span>
                                  <span className="text-lg font-semibold text-primary" data-testid="booking-effective-price">
                                    {Number(effectivePrice).toLocaleString(numberLocale())} <span className="text-xs font-normal text-ink-muted">THB</span>
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {hasAirportZones && (
                            <div className="space-y-3" data-testid="booking-pickup-fields">
                              <p className="text-sm font-semibold text-primary">{t('transferBooking.form.title')}</p>
                              {isZoneToZone ? null : dialogFromAirport ? (
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label htmlFor="booking-flight" className="text-xs font-medium text-ink-light mb-1 block">{t('transferBooking.form.flightNumberRequired')} *</Label>
                                    <Input id="booking-flight" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)}
                                      placeholder={t('transferBooking.form.flightPlaceholder')} autoCapitalize="characters" autoComplete="off"
                                      className="input-field h-11" data-testid="booking-flight-input" />
                                    {pickupErr('flightNumber')}
                                  </div>
                                  <div>
                                    <Label htmlFor="booking-arrival" className="text-xs font-medium text-ink-light mb-1 block">{t('transferBooking.form.landingTime')} *</Label>
                                    <Input id="booking-arrival" type="time" value={flightArrival} onChange={(e) => setFlightArrival(e.target.value)}
                                      className="input-field h-11" data-testid="booking-arrival-input" />
                                    {pickupErr('flightArrival')}
                                  </div>
                                  <div className="col-span-2 rounded-lg border border-accent/30 bg-accent/5 p-3 text-sm space-y-1" data-testid="booking-computed-pickup">
                                    {arrivalPickup ? (
                                      <>
                                        <p className="flex justify-between gap-3" data-testid="booking-landing-line">
                                          <span className="text-ink-light">{t('transferBooking.form.landingLine')}</span>
                                          <span className="font-semibold text-primary text-right whitespace-nowrap">{dayLabel(bookingDate)} · {clockLabel(flightArrival, i18n.language)}</span>
                                        </p>
                                        <p className="flex justify-between gap-3" data-testid="booking-pickup-line">
                                          <span className="text-ink-light">{t('transferBooking.form.pickupLine')}</span>
                                          <span className="font-semibold text-primary text-right whitespace-nowrap">{dayLabel(arrivalPickup.date)} · {clockLabel(arrivalPickup.time, i18n.language)}</span>
                                        </p>
                                      </>
                                    ) : (
                                      <p className="text-ink-light" data-testid="booking-pickup-pending">{t('transferBooking.form.pickupPending')}</p>
                                    )}
                                    <p className="text-[11px] text-ink-muted" data-testid="booking-pickup-rule">{t('transferBooking.form.pickupRule', { minutes: PICKUP_AFTER_LANDING_MINUTES })}</p>
                                    <p className="text-[11px] text-ink-muted">{t('transferBooking.form.delayNote')}</p>
                                  </div>
                                  <div className="col-span-2 -mt-1">
                                    {noticeBlock}
                                    <p className="text-[11px] text-ink-muted mt-1">{t('transferBooking.form.manualConfirm')}</p>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <Label htmlFor="booking-flight" className="text-xs font-medium text-ink-light mb-1 block">{t('transferBooking.form.flightNumber')}</Label>
                                  <Input id="booking-flight" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)}
                                    placeholder={t('transferBooking.form.flightPlaceholder')} autoCapitalize="characters" autoComplete="off"
                                    className="input-field h-11" data-testid="booking-flight-input" />
                                  {pickupErr('flightNumber')}
                                </div>
                              )}
                              <div>
                                <Label htmlFor="booking-place" className="text-xs font-medium text-ink-light mb-1 block">
                                  {t(isZoneToZone ? 'transferBooking.form.pickupExact' : dialogFromAirport ? 'transferBooking.form.dropoff' : 'transferBooking.form.pickup')} *
                                </Label>
                                <Input id="booking-place" value={placeText} onChange={(e) => setPlaceText(e.target.value)} maxLength={200}
                                  placeholder={t(isZoneToZone ? 'transferBooking.form.addressPlaceholder' : 'transferBooking.form.placePlaceholder')} autoComplete="off"
                                  className="input-field h-11" data-testid="booking-place-input" />
                                {pickupErr('place')}
                              </div>
                              {isZoneToZone && (
                                <div>
                                  <Label htmlFor="booking-dropoff" className="text-xs font-medium text-ink-light mb-1 block">{t('transferBooking.form.dropoffExact')} *</Label>
                                  <Input id="booking-dropoff" value={dropoffText} onChange={(e) => setDropoffText(e.target.value)} maxLength={200}
                                    placeholder={t('transferBooking.form.addressPlaceholder')} autoComplete="off"
                                    className="input-field h-11" data-testid="booking-dropoff-input" />
                                  {pickupErr('dropoff')}
                                </div>
                              )}
                              {transferDirection === 'to_airport' && (
                                <>
                                  <div>
                                    <Label htmlFor="booking-room" className="text-xs font-medium text-ink-light mb-1 block">
                                      {t('transferBooking.form.room')}{transferRoomUnknown ? '' : ' *'}
                                    </Label>
                                    <Input id="booking-room" value={transferRoom} onChange={(e) => setTransferRoom(e.target.value)} maxLength={MAX_ROOM}
                                      placeholder={t('transferBooking.form.roomPlaceholder')} autoComplete="off" disabled={transferRoomUnknown}
                                      className="input-field h-11" data-testid="booking-room-input" />
                                    <div className="mt-1.5 flex items-start gap-2" data-testid="booking-room-unknown">
                                      <RidaloCheckbox id="booking-room-unknown" checked={transferRoomUnknown}
                                        onCheckedChange={(v) => { setTransferRoomUnknown(v === true); if (v === true) setTransferRoom(''); }}
                                        data-testid="booking-room-unknown-checkbox" />
                                      <Label htmlFor="booking-room-unknown" className="text-xs text-ink-light font-normal cursor-pointer leading-5">
                                        {t('transferBooking.form.roomUnknown')}
                                      </Label>
                                    </div>
                                    {transferRoomUnknown && (
                                      <p className="text-[11px] text-amber-700 mt-1" data-testid="booking-room-unknown-hint">{t('transferBooking.form.roomUnknownHint')}</p>
                                    )}
                                    {pickupErr('room')}
                                  </div>
                                  <div>
                                    <Label htmlFor="booking-maps" className="text-xs font-medium text-ink-light mb-1 block">{t('transferBooking.form.mapsUrl')} *</Label>
                                    <Input id="booking-maps" type="url" inputMode="url" value={hotelMapsUrl} onChange={(e) => setHotelMapsUrl(e.target.value)} maxLength={1000}
                                      placeholder="https://maps.app.goo.gl/…" autoComplete="off" autoCapitalize="off" spellCheck={false}
                                      className="input-field h-11" data-testid="booking-maps-input" />
                                    <p className="text-[11px] text-ink-muted mt-1" data-testid="booking-maps-help">{t('transferBooking.form.mapsHelp')}</p>
                                    {pickupErr('mapsUrl')}
                                  </div>
                                </>
                              )}
                              <div>
                                <Label htmlFor="booking-luggage" className="text-xs font-medium text-ink-light mb-1 block">{t('transferBooking.form.luggage')} *</Label>
                                <Input id="booking-luggage" type="number" inputMode="numeric" min="0" max={capacity.luggageMax ?? MAX_LUGGAGE} value={luggage}
                                  onChange={(e) => setLuggage(e.target.value)} placeholder="0"
                                  className="input-field h-11" data-testid="booking-luggage-input" />
                                {pickupErr('luggage')}
                              </div>
                              <p className="text-[11px] text-ink-muted" data-testid="booking-contact-hint">{t('transferBooking.form.contactHint')}</p>
                            </div>
                          )}
                        </>
                      )}
                      <div>
                        <Label htmlFor="booking-details" className="text-sm font-semibold text-primary mb-2 block">
                          {t('serviceDetail.booking.notesLabel')} <span className="text-ink-muted font-normal">{t('serviceDetail.booking.optional')}</span>
                        </Label>
                        <Textarea
                          id="booking-details"
                          value={bookingDetails}
                          onChange={(e) => setBookingDetails(e.target.value)}
                          placeholder={t(isExcursion ? 'serviceDetail.booking.notesPlaceholderExcursion' : 'serviceDetail.booking.notesPlaceholder')}
                          className="input-field min-h-24 resize-none"
                          data-testid="booking-details-input"
                        />
                      </div>
                      {hasAirportZones && effectiveRouteText && (
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="booking-transfer-summary">
                          <p className="font-semibold text-primary">{t('transferBooking.summary.title')}</p>
                          {[
                            ['route', t('transferBooking.summary.route'), effectiveRouteText],
                            ...(dialogFromAirport
                              ? [['landing', t('transferBooking.summary.landing'), bookingDate && flightArrival
                                  ? `${dayLabel(bookingDate)} · ${clockLabel(flightArrival, i18n.language)}` : '—'],
                                 ['when', t('transferBooking.summary.pickupPlanned'), arrivalPickup
                                  ? `${dayLabel(arrivalPickup.date)} · ${clockLabel(arrivalPickup.time, i18n.language)}` : '—']]
                              : [['when', t('transferBooking.summary.pickup'), bookingDate && bookingTime
                                  ? `${dayLabel(bookingDate)} · ${bookingTime}` : '—']]),
                            ['passengers', t('transferBooking.summary.passengers'), String(passengers)],
                            ['luggage', t('transferBooking.summary.luggage'), luggage === '' ? '—' : String(luggage)],
                            ...(flightNumber.trim() ? [['flight', t('transferBooking.summary.flight'), normalizeFlight(flightNumber)]] : []),
                            ['place', t(dialogFromAirport ? 'transferBooking.summary.dropoff' : 'transferBooking.summary.pickupPlace'), placeText.trim() || '—'],
                            ...(isZoneToZone ? [['dropoff', t('transferBooking.summary.dropoff'), dropoffText.trim() || '—']] : []),
                            ...(transferDirection !== 'to_airport' ? [] : [
                              ['room', t('transferBooking.summary.room'), transferRoomUnknown ? t('transferBooking.show.roomToComplete') : (transferRoom.trim() || '—')],
                              ['maps', t('transferBooking.summary.maps'), cleanMapsUrl(hotelMapsUrl) ? t('transferBooking.summary.mapsGiven') : '—'],
                            ]),
                            ['price', t('transferBooking.summary.price'), effectivePrice != null ? `${Number(effectivePrice).toLocaleString(numberLocale())} THB` : '—'],
                          ].map(([key, label, value]) => (
                            <div key={key} className="flex justify-between gap-3" data-testid={`booking-summary-${key}`}>
                              <span className="text-ink-muted shrink-0">{label}</span>
                              <span className="font-medium text-primary text-right break-words min-w-0">{value}</span>
                            </div>
                          ))}
                          {dialogFromAirport && (
                            <p className="text-[11px] text-ink-muted pt-1" data-testid="booking-summary-rule">{t('transferBooking.form.pickupRule', { minutes: PICKUP_AFTER_LANDING_MINUTES })}</p>
                          )}
                          <p className="text-[11px] text-ink-muted pt-1">{t('transferBooking.summary.serverPrice')}</p>
                        </div>
                      )}
                      {(isTransfer || isPrivateDriver) && (() => {
                        // Deposit conditions known before the request is sent.
                        const price = isPrivateDriver ? privateDriverPrice : effectivePrice;
                        const amount = depositAmount(price, transportDepositPct);
                        return (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-primary" data-testid="booking-deposit-terms">
                            {transportDepositPct > 0
                              ? (amount > 0
                                  ? t('transportDeposit.bookingTerms', { pct: transportDepositPct, amount: amount.toLocaleString(numberLocale()) })
                                  : t('transportDeposit.bookingTermsNoPrice', { pct: transportDepositPct }))
                              : t('transportDeposit.bookingNone')}
                          </div>
                        );
                      })()}
                      <Button
                        onClick={handleBooking}
                        className="btn-primary w-full h-11"
                        disabled={bookingLoading}
                        data-testid="confirm-booking-button"
                      >
                        {bookingLoading ? t('serviceDetail.booking.submitting') : t('serviceDetail.booking.submit')}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <BookingRequestSentDialog
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  bookingId={createdBookingId}
                  continuePath={isPrivateDriver ? '/transport?tab=private_driver'
                    : isTransfer ? '/transport'
                    : isExcursion ? '/services?category=excursion' : '/services'}
                />

                <div className="text-xs text-ink-light text-center">
                  {isExcursion
                    ? (excursionPaymentPct >= 100
                        ? t('serviceDetail.sidebar.footerFull')
                        : t('serviceDetail.sidebar.footerDepositExcursion', { pct: excursionPaymentPct }))
                    : ((isTransfer || isPrivateDriver)
                    ? (transportDepositPct > 0
                        ? t('serviceDetail.sidebar.footerDepositTransfer', { pct: transportDepositPct })
                        : t('serviceDetail.sidebar.footerNoDeposit'))
                    : (((service.details || {}).payment_config || {}).deposit_required === false
                        ? t('serviceDetail.sidebar.footerNoDeposit')
                        : t('serviceDetail.sidebar.footerDefault')))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guest login/signup prompt — appears when a non-authenticated visitor
          attempts to submit a Transport booking. The form state has been
          persisted in localStorage so the user returns to their filled form. */}
      {/* Phones / tablets: fixed booking bar (same price as the booking card; opens the same form). */}
      <MobileBookingBar
        targetTestId="open-booking-button"
        label={(isStartingFrom || isPrivateDriverStartingFrom) ? t('serviceDetail.sidebar.startingFrom') : (isExcursion ? t('serviceDetail.sidebar.adultFrom') : t('serviceDetail.sidebar.price'))}
        price={(() => { const v = Number(isExcursion ? (excursionAdultPrice ?? service?.price ?? 0) : displayPrice); return v > 0 ? v.toLocaleString(numberLocale()) : null; })()}
        unit="THB"
      />

      <Dialog open={authPromptOpen} onOpenChange={setAuthPromptOpen}>
        <DialogContent className="max-w-md" data-testid="auth-prompt-modal">
          <DialogHeader>
            <DialogTitle>{t('serviceDetail.authPrompt.title')}</DialogTitle>
            <DialogDescription>
              {t('serviceDetail.authPrompt.subtitle')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="rounded-xl bg-background-soft p-3 text-xs text-ink-light">
              <p className="font-semibold text-primary mb-1">{t('serviceDetail.authPrompt.recap')}</p>
              {bookingDate ? (
                <p data-testid="auth-prompt-recap">
                  {t('serviceDetail.authPrompt.recapLine', { date: format(new Date(`${bookingDate}T00:00:00`), 'EEEE d MMMM yyyy', { locale: dateLocale }), time: bookingTime, count: passengers })}
                  {effectivePrice != null && t('serviceDetail.authPrompt.recapPriceSuffix', { price: Number(effectivePrice).toLocaleString(numberLocale()) })}
                </p>
              ) : (
                <p className="italic">{t('serviceDetail.authPrompt.noRoute')}</p>
              )}
            </div>
            <Button
              onClick={() => { saveDraft(); navigate(`/login?redirect=/services/${serviceId}`); }}
              className="btn-primary w-full"
              data-testid="auth-prompt-login"
            >
              {t('serviceDetail.authPrompt.login')}
            </Button>
            <Button
              onClick={() => { saveDraft(); navigate(`/register?redirect=/services/${serviceId}`); }}
              variant="outline"
              className="w-full"
              data-testid="auth-prompt-signup"
            >
              {t('serviceDetail.authPrompt.signup')}
            </Button>
            <p className="text-xs text-ink-muted text-center">
              {t('serviceDetail.authPrompt.prefillNote')}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};